import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import not_found from "../../../assets/images/sattva_misc/not_found.jpg"
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const NewsSidebar = () => {
  const [newsList, setNewsList] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const [editorToken, setEditorToken] = useState(null);

  useEffect(() => {
    setEditorToken(localStorage.getItem("Editor_token"));
  }, []);

  const isEditor = Boolean(editorToken);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const res = await fetch("https://sattva-backend.demetrix.in/news");
      const data = await res.json();

      if (data.status === "success") {
        setNewsList(
          data.data.filter((item) => item.is_published && !item.is_deleted)
        );
      } else {
        setNewsList([]);
      }
    } catch (error) {
      console.error(error);
      // alert("Error fetching news");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const filteredNews = newsList.filter(
    (n) =>
      n.title?.toLowerCase().includes(search.toLowerCase()) ||
      n.source?.toLowerCase().includes(search.toLowerCase())
  );

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditingNews((prev) => ({ ...prev, [name]: value }));
  };

  const handleMediaChange = (index, field, value) => {
    const updated = [...editingNews.media];
    updated[index][field] = value;
    setEditingNews((prev) => ({ ...prev, media: updated }));
  };

  const addMedia = () => {
    setEditingNews((prev) => ({
      ...prev,
      media: [...(prev.media || []), { type: "image", url: "", caption: "" }],
    }));
  };

  const removeMedia = (index) => {
    setEditingNews((prev) => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!isEditor) {
      alert("You are not authorized to perform this action.");
      return;
    }

    try {
      const isEdit = newsList.some((n) => n.slug === editingNews.slug);
      const url = isEdit
        ? `https://sattva-backend.demetrix.in/news/${editingNews.slug}`
        : `https://sattva-backend.demetrix.in/news`;

      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${editorToken}`,
        },
        body: JSON.stringify(editingNews),
      });

      const data = await res.json();

      alert(data.message || "Operation completed");

      if (data.status === "success") {
        setModalOpen(false);
        setEditingNews(null);
        fetchNews();
      }
    } catch (error) {
      console.error(error);
      alert("Error saving news: " + error.message);
    }
  };

  const handleDelete = async (slug) => {
    if (!isEditor) {
      alert("You are not authorized to delete news.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this news? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      const res = await fetch(`https://sattva-backend.demetrix.in/news/${slug}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${editorToken}`,
        },
      });

      const data = await res.json();

      alert(data.message || "Delete operation completed");

      if (data.status === "success") {
        fetchNews();
      }
    } catch (error) {
      console.error(error);
      alert("Error deleting news: " + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 py-6">
        {[...Array(5)].map((_, idx) => (
          <div key={idx} className="animate-pulse flex flex-col gap-2 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
            <div className="h-5 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }


  return (
    <section className="w-full">

      {/* Header: Search + Add */}
      <div className="flex items-center gap-2 mb-6">
        {
          newsList?.length > 0 &&

          <input
            type="text"
            placeholder="Search news..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 border border-gray-300 dark:border-gray-700 rounded-lg p-2 text-sm 
                     focus:outline-none focus:ring-2 focus:ring-green-500 
                     dark:bg-gray-900 dark:text-white"
          />
        }

        {isEditor && (
          <button
            onClick={() => {
              setEditingNews({
                slug: `new-news-${Date.now()}`,
                title: "",
                summary: "",
                source: "",
                content: "",
                media: [],
              });
              setModalOpen(true);
            }}
            className="p-2 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition"
            title="Add News"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* News List – same layout, just better dark mode text */}
      {
        newsList?.length > 0 ?
          <div className="flex flex-col gap-6">
            {filteredNews?.map((news) => (
              <div
                key={news.slug}
                className="relative group border border-gray-200 dark:border-gray-700 rounded-xl p-3 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div onClick={() => navigate(`/news/${news.slug}`)}>
                  <h4 className="font-semibold mb-1 text-gray-900 dark:text-white">
                    {news.title}
                  </h4>
                  <span className="text-xs border border-green-600 dark:border-green-500 px-3 py-1 rounded-full 
                             text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950">
                    {news.source}
                  </span>
                </div>

                {/* Edit / Delete */}
                {isEditor && (
                  <div className="absolute top-2 right-2 flex gap-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingNews(news);
                        setModalOpen(true);
                      }}
                      className="p-1 rounded-full bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4 text-blue-700 dark:text-blue-300" />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(news.slug);
                      }}
                      className="p-1 rounded-full bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4 text-red-700 dark:text-red-300" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {filteredNews.length === 0 && search && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                No news found
              </p>
            )}
          </div> : <div className="w-full flex items-center justify-center px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-8 max-w-md w-full text-center"
            >
              {/* Illustration */}
              <motion.img
                src={not_found}
                alt="No news"
                className="mx-auto h-40 mb-6"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Text */}
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
                No news found
              </h3>

              {/* <p className="text-slate-500 dark:text-slate-400 mb-6">
                We couldn’t find any news here. Once content is published, it’ll show up right away.
              </p> */}

              {/* CTA */}
              <div className="flex justify-center gap-3">
                <Link
                  to="/"
                  className="px-5 py-2.5 rounded-lg text-sm font-medium
              bg-slate-900 text-white dark:bg-white dark:text-slate-900
              hover:opacity-90 transition"
                >
                  Go Home
                </Link>
              </div>
            </motion.div>
          </div>

      }

      {/* Modal – added dark mode support */}
      {modalOpen && editingNews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto mt-10">
            <h3 className="text-xl font-semibold mb-4 dark:text-white">
              {newsList.some((n) => n.slug === editingNews.slug) ? "Edit News" : "Add News"}
            </h3>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <input
                name="title"
                value={editingNews.title || ""}
                onChange={handleFormChange}
                placeholder="Title"
                className="w-full border rounded-lg p-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                required
              />
              <textarea
                name="content"
                value={editingNews.content || ""}
                onChange={handleFormChange}
                placeholder="Content"
                rows={6}
                className="w-full border rounded-lg p-2 resize-none dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                required
              />

              <input
                name="summary"
                value={editingNews.summary || ""}
                onChange={handleFormChange}
                placeholder="Summary"
                className="w-full border rounded-lg p-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
              />

              <input
                name="source"
                value={editingNews.source || ""}
                onChange={handleFormChange}
                placeholder="Source"
                className="w-full border rounded-lg p-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
              />

              {/* Media */}
              <div>
                <h4 className="font-semibold mb-2 dark:text-white">Media</h4>

                {(editingNews.media || []).map((m, idx) => (
                  <div key={idx} className="border rounded-lg p-2 mb-2 dark:border-gray-700">
                    <select
                      value={m.type}
                      onChange={(e) => handleMediaChange(idx, "type", e.target.value)}
                      className="w-full border rounded-lg p-1 mb-1 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>

                    <input
                      value={m.url || ""}
                      onChange={(e) => handleMediaChange(idx, "url", e.target.value)}
                      placeholder="Media URL"
                      className="w-full border rounded-lg p-1 mb-1 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                    />

                    <input
                      value={m.caption || ""}
                      onChange={(e) => handleMediaChange(idx, "caption", e.target.value)}
                      placeholder="Caption"
                      className="w-full border rounded-lg p-1 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                    />

                    <button
                      type="button"
                      onClick={() => removeMedia(idx)}
                      className="text-red-600 text-sm mt-1 hover:text-red-800"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {(editingNews.media || []).length < 5 && (
                  <button
                    type="button"
                    onClick={addMedia}
                    className="bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800"
                  >
                    + Add Media
                  </button>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setEditingNews(null);
                  }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default NewsSidebar;